import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import { ArrowRight, Copy, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DIR_LABELS = { N: "NORTH", E: "EAST", S: "SOUTH", W: "WEST" };

export default function DiscountPopup({ open, direction, onClose, onUnlocked }) {
  const [stage, setStage] = useState("email");
  const [email, setEmail] = useState("");
  const [reward, setReward] = useState(null);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/leads`, { email, direction });
      setReward(data);
      setStage("reveal");
      onUnlocked(data);
    } catch {
      setError("The stars didn't answer — check your email and try again.");
    } finally {
      setSending(false);
    }
  };

  const copy = () => {
    if (!reward) return;
    navigator.clipboard?.writeText(reward.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="discount-popup"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 p-6 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 60, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -30 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="grain relative w-[min(92vw,430px)] border border-ink/10 bg-cream p-8 text-ink shadow-2xl md:p-10"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              data-testid="close-popup-button"
              aria-label="Close"
              onClick={onClose}
              className="absolute right-4 top-4 text-ink/40 transition-colors hover:text-blush"
            >
              <X size={18} />
            </button>

            {stage === "email" && (
              <form onSubmit={submit} data-testid="email-form">
                <p className="font-mono text-[10px] tracking-[0.4em] text-ink/50">
                  {DIR_LABELS[direction]} STAR — WAITING AT CHECKOUT
                </p>
                <h2 className="mt-3 font-script text-5xl leading-none text-ink">Almost yours,</h2>
                <p className="mt-3 text-sm text-ink/60">
                  Leave your email and your {DIR_LABELS[direction].toLowerCase()} star reveals the
                  discount it hid for you.
                </p>
                <input
                  data-testid="email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@somewhere.com"
                  className="mt-6 w-full border-b border-ink/30 bg-transparent py-3 font-mono text-sm tracking-wider outline-none transition-colors placeholder:text-ink/30 focus:border-blush"
                />
                {error && (
                  <p data-testid="email-error" className="mt-3 font-mono text-[11px] text-blush">
                    {error}
                  </p>
                )}
                <button
                  data-testid="email-submit"
                  type="submit"
                  disabled={sending}
                  className="mt-6 flex w-full items-center justify-center gap-3 bg-ink py-4 font-mono text-[11px] tracking-[0.35em] text-cream transition-colors hover:bg-blush disabled:opacity-50"
                >
                  {sending ? "ASKING THE STARS…" : "UNLOCK MY DISCOUNT"}
                  <ArrowRight size={14} />
                </button>
              </form>
            )}

            {stage === "reveal" && reward && (
              <div data-testid="reveal-panel">
                <p className="font-mono text-[10px] tracking-[0.4em] text-ink/50">
                  {reward.label.toUpperCase()} STAR UNLOCKED
                </p>
                <h2
                  data-testid="discount-percent"
                  className="mt-2 font-script text-7xl leading-none text-blush"
                >
                  {reward.percent}% off
                </h2>
                <p className="mt-2 text-sm text-ink/60">Your personal code, fresh from the aura:</p>
                <div className="mt-5 flex items-center justify-between border border-dashed border-ink/40 px-5 py-4">
                  <span data-testid="discount-code" className="font-mono text-lg font-bold tracking-[0.2em]">
                    {reward.code}
                  </span>
                  <button
                    data-testid="copy-code-button"
                    type="button"
                    onClick={copy}
                    className="flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] text-ink/60 transition-colors hover:text-blush"
                  >
                    <Copy size={14} />
                    {copied ? "COPIED" : "COPY"}
                  </button>
                </div>
                <button
                  data-testid="back-to-shop-button"
                  type="button"
                  onClick={onClose}
                  className="mt-6 flex w-full items-center justify-center gap-3 bg-ink py-4 font-mono text-[11px] tracking-[0.35em] text-cream transition-colors hover:bg-blush"
                >
                  BACK TO SHOPPING
                  <ArrowRight size={14} />
                </button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
